import { lazy, Suspense } from 'react';
import SEOHead from '../../components/feature/SEOHead';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import FloatingButtons from '../../components/feature/FloatingButtons';

// 延遲載入首屏以外的區塊
const AboutSection = lazy(() => import('./components/AboutSection'));
const NewsSection = lazy(() => import('./components/NewsSection'));
const ServicesSection = lazy(() => import('./components/ServicesSection'));
const PricingSection = lazy(() => import('./components/PricingSection'));
const ActivitiesSection = lazy(() => import('./components/ActivitiesSection'));
const CTASection = lazy(() => import('./components/CTASection'));
const TransportSection = lazy(() => import('./components/TransportSection'));
const ContactSection = lazy(() => import('./components/ContactSection'));
const FAQSection = lazy(() => import('./components/FAQSection'));
const Footer = lazy(() => import('./components/Footer'));

const SectionLoader = () => (
  <div className="min-h-[300px] flex items-center justify-center">
    <div className="w-8 h-8 border-3 border-baby-blue-200 border-t-baby-blue-500 rounded-full animate-spin"></div>
  </div>
);

const HomePage = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "前金日間照顧中心的服務時間是什麼時候？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "前金日間照顧中心的服務時間為週一至週五 08:00-17:00，國定假日休息。歡迎於服務時間內來電洽詢或預約參觀。"
        }
      },
      {
        "@type": "Question",
        "name": "前金日間照顧中心提供哪些照護服務？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "前金日間照顧中心提供長照3.0日間照顧服務，包含失智照護、失能照護、認知訓練、健康促進活動、營養午餐與點心、個別化訓練、自立支援、沐浴服務及交通接送等完整照護方案。"
        }
      },
      {
        "@type": "Question",
        "name": "前金日間照顧中心的地址在哪裡？交通方便嗎？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "前金日間照顧中心位於高雄市前金區文武二街163號5樓，鄰近前金捷運站3號出口，步行約3分鐘即可到達，交通非常便利。自行開車者附近也有收費停車場。"
        }
      },
      {
        "@type": "Question",
        "name": "前金日間照顧中心可以申請長照補助嗎？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "可以的。前金日間照顧中心配合政府長照3.0政策，符合資格的長者可申請長照補助，最高補助84%。我們的專業團隊也會協助家屬辦理補助申請相關事宜。"
        }
      },
      {
        "@type": "Question",
        "name": "如何預約參觀前金日間照顧中心？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "您可以透過電話 07-2416568 預約參觀，或在官網填寫預約參觀表單。建議提前3天預約，我們將安排專人為您導覽介紹中心環境與服務內容。參觀時間為週一至週五 09:00-17:00。"
        }
      },
      {
        "@type": "Question",
        "name": "前金日間照顧中心的服務對象有哪些？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "前金日間照顧中心主要服務對象為失智個案及失能個案。我們擁有專業的照護團隊，針對不同長者的需求提供個別化的照護計畫，讓每位長者都能獲得最適切的照顧。"
        }
      }
    ]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://qianjin-daycare.com/#webpage",
    "url": "https://qianjin-daycare.com",
    "name": "前金日間照顧中心｜高雄市前金區長照3.0失智失能照護",
    "description": "高雄市前金區專業日間照顧中心，提供長照3.0失智失能照護服務，鄰近前金捷運站，政府補助最高84%。服務項目包含生活照顧、健康促進、認知訓練、交通接送。",
    "isPartOf": {
      "@type": "WebSite",
      "@id": "https://qianjin-daycare.com/#website",
      "url": "https://qianjin-daycare.com",
      "name": "前金日間照顧中心",
      "publisher": {
        "@id": "https://qianjin-daycare.com/#organization"
      }
    },
    "about": {
      "@id": "https://qianjin-daycare.com/#organization"
    },
    "primaryImageOfPage": {
      "@type": "ImageObject",
      "url": "https://static.readdy.ai/image/e6fb9fe30ffa661384912f3cccf551e7/6f9d341714cc3a0c10c20a6664ce2de5.png"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首頁",
          "item": "https://qianjin-daycare.com"
        }
      ]
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "日間照顧服務",
    "provider": {
      "@id": "https://qianjin-daycare.com/#organization"
    },
    "areaServed": {
      "@type": "City",
      "name": "高雄市"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "長照3.0日間照顧服務",
      "itemListElement": [
        {
          "@type": "OfferCatalog",
          "name": "照護服務",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "失智照護",
                "description": "專業失智症照護，延緩認知功能退化"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "失能照護",
                "description": "協助日常生活起居，維持身體機能"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "認知訓練",
                "description": "益智遊戲、手作活動等認知刺激課程"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "健康促進",
                "description": "體適能活動、健康檢測、營養餐飲"
              }
            }
          ]
        }
      ]
    }
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://qianjin-daycare.com/#website",
    "name": "前金日間照顧中心",
    "alternateName": "前金日間照顧中心",
    "url": "https://qianjin-daycare.com/",
    "publisher": {
      "@id": "https://qianjin-daycare.com/#organization"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://qianjin-daycare.com/#organization",
    "name": "前金日間照顧中心",
    "alternateName": "前金社區日間照顧機構",
    "url": "https://qianjin-daycare.com/",
    "logo": {
      "@type": "ImageObject",
      "@id": "https://qianjin-daycare.com/#logo",
      "url": "https://static.readdy.ai/image/e6fb9fe30ffa661384912f3cccf551e7/d5aea6c2e30695f7c421b6c6d03bc7cc.png",
      "contentUrl": "https://static.readdy.ai/image/e6fb9fe30ffa661384912f3cccf551e7/d5aea6c2e30695f7c421b6c6d03bc7cc.png",
      "caption": "前金日間照顧中心"
    },
    "image": {
      "@id": "https://qianjin-daycare.com/#logo"
    },
    "telephone": "07-2416568",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "文武二街163號5樓",
      "addressLocality": "前金區",
      "addressRegion": "高雄市",
      "postalCode": "801",
      "addressCountry": "TW"
    },
    "sameAs": [
      "https://www.facebook.com/p/前金社區日間照顧機構-100063908289224/"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="前金日間照顧中心｜高雄市前金區長照3.0失智失能照護"
        description="高雄市前金區專業日間照顧中心，提供長照3.0失智失能照護服務，鄰近前金捷運站，政府補助最高84%。服務項目包含生活照顧、健康促進、認知訓練、交通接送。電話：07-2416568"
        keywords="前金日間照顧中心,高雄日照中心,前金區日照,長照3.0,失智照護,失能照護"
        canonical="https://qianjin-daycare.com"
        lastModified="2025-07-15"
        jsonLd={[organizationSchema, websiteSchema, webPageSchema, serviceSchema, faqSchema]}
      />
      <Navbar />
      <HeroSection />
      <Suspense fallback={<SectionLoader />}>
        <AboutSection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <NewsSection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <ServicesSection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <PricingSection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <ActivitiesSection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <CTASection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <TransportSection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <ContactSection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <FAQSection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <Footer />
      </Suspense>
      <FloatingButtons />
    </div>
  );
};

export default HomePage;
